import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, Play, Plus, Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import defaultNoteCover from '../assets/note-covers/default-note-cover.svg';
import ConfirmDialog from './ConfirmDialog';
import NoteDetailHeader from './NoteDetailHeader';
import MapNoteActions from './MapNoteActions';
import VocabularyDirectionMenuItems, { vocabularyDirectionLabel } from './VocabularyDirectionMenuItems';
import VocabularyWordCard from './VocabularyWordCard';
import VocabularyNoteStats from './VocabularyNoteStats';
import CardFaceToggle from './CardFaceToggle';
import { getNoteThemeStyle } from '../utils/noteTheme';
import { stageVocabularySession } from '../services/VocabularyLearningSession';
import { unlockListeningAudio } from '../services/ListeningAudioTransport';
import './VocabularyPage.css';
import './VocabularyNoteDetailPage.css';

const v = (key, values) => t(`vocabulary.${key}`, undefined, values);
const PAGE_SIZE = 10;

export default function VocabularyNoteDetailPage() {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cover, setCover] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showAnswersFirst, setShowAnswersFirst] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [notice, setNotice] = useState('');
  const listRef = useRef(null);
  const pageFocusRef = useRef(false);
  const alive = useRef(false);
  const inFlight = useRef(false);
  const loadSequence = useRef(0);
  const retryOperation = useRef(null);
  const refresh = useCallback(async () => {
    const sequence = ++loadSequence.current;
    try {
      const next = await apiClient.getVocabularyNote(noteId);
      if (alive.current && sequence === loadSequence.current) { setNote(next); setLoading(false); }
    } catch (err) { if (alive.current && sequence === loadSequence.current) throw err; }
  }, [noteId]);
  useEffect(() => {
    alive.current = true; setNote(null); setLoading(true); setSearch(''); setPage(0); setError(''); setNotice(''); setShowAnswersFirst(true);
    refresh().catch(err => { if (alive.current) { retryOperation.current = refresh; setError(err.message || v('failed')); setLoading(false); } });
    return () => { alive.current = false; };
  }, [refresh]);
  useEffect(() => {
    let canceled = false; let objectUrl;
    setCover(null);
    if (note?.coverUrl) apiClient.fetchVocabularyCover(noteId).then(blob => {
      if (canceled) return;
      objectUrl = URL.createObjectURL(blob); setCover(objectUrl);
    }).catch(() => { /* Retain the common fallback cover. */ });
    return () => { canceled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [noteId, note?.coverUrl]);
  const perform = async task => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(''); retryOperation.current = task;
    try { await task(); if (alive.current) retryOperation.current = null; }
    catch (err) { if (alive.current) setError(err.message || v('networkFailed')); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  };
  const start = () => {
    if (inFlight.current) return;
    void unlockListeningAudio();
    const operationId = uuid(); let payload;
    void perform(async () => {
      if (!payload) payload = { operationId, startNoteId: noteId, scoringRuleVersion: 3 };
      const session = await apiClient.startVocabularySession(payload);
      if (alive.current) {
        if (session.status === 'active') navigate('/vocabulary/practice', { state: stageVocabularySession(session) });
        else await refresh();
      }
    });
  };
  const toggle = () => {
    const enabled = note.isEnabled === false; const operationId = uuid();
    void perform(async () => { await apiClient.setNoteEnabled(noteId, enabled, { studyKind: 'vocabulary', operationId }); await refresh(); });
  };
  const changeDirection = direction => {
    if (inFlight.current || (note.vocabularyDirection || 'q_to_a') === direction) return;
    const operationId = uuid(); setNotice('');
    void perform(async () => {
      await apiClient.setVocabularyNoteDirection(noteId, direction, { operationId });
      await refresh();
      if (alive.current) setNotice(v('directionSaved'));
    });
  };
  const remove = () => {
    const operationId = uuid(); setDeleteOpen(false);
    void perform(async () => { await apiClient.deleteNote(noteId, { studyKind: 'vocabulary', operationId }); if (alive.current) navigate('/vocabulary', { replace: true }); });
  };
  const title = note?.title || note?.noteTitle || v('title');
  const source = note?.sourceType || note?.source || (note?.sourceMarketListingId ? 'market' : note?.sourcePresetId ? 'official' : 'custom');
  const original = ['custom', 'personal'].includes(source) && !note?.sourcePresetId && !note?.sourceMarketListingId;
  const cards = useMemo(() => (note?.cards || []).map((card, index) => ({ ...card, ordinal: card.ordinal || index + 1 })), [note?.cards]);
  const query = search.trim().toLocaleLowerCase();
  const filtered = cards.filter(card => `${card.front || card.Q || ''} ${card.back || card.A || ''} ${card.backMemo || ''}`.toLocaleLowerCase().includes(query));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visiblePage = Math.min(page, totalPages - 1);
  useEffect(() => {
    if (!pageFocusRef.current) return;
    pageFocusRef.current = false;
    listRef.current?.querySelector('button')?.focus({ preventScroll: true });
    listRef.current?.scrollIntoView?.({ block: 'start' });
  }, [visiblePage]);
  const changePage = next => { pageFocusRef.current = true; setPage(next); };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cards.map(card => [card.cardType === 'simple' || card.cardType === 'word' ? '@word: true' : null, `@Q: ${card.Q ?? card.front ?? ''}`, `@A: ${card.A ?? card.back ?? ''}`, card.backMemo ? `@back: ${card.backMemo}` : null].filter(Boolean).join('\n')).join('\n\n'));
      if (alive.current) setCopySuccess(true);
    } catch { if (alive.current) setError(v('copyFailed')); }
  };
  const theme = getNoteThemeStyle(note || {});
  return <main className="vocabulary-note-detail" aria-busy={busy}>
    <div className={`note-detail-page${theme ? ' has-note-theme' : ''}${note?.isEnabled === false ? ' is-note-disabled' : ''}`} style={theme} aria-hidden={deleteOpen ? true : undefined} inert={deleteOpen ? '' : undefined}>
      {!note && <button type="button" className="btn-back-inline" aria-label={v('backBooks')} onClick={() => navigate('/vocabulary')}><ArrowLeft size={22} /></button>}
      {loading && <p role="status">{v('loading')}</p>}
      {error && <div className="vocabulary-error" role="alert"><p>{error}</p><button type="button" disabled={busy} onClick={() => void perform(retryOperation.current || refresh)}>{v('retry')}</button></div>}
      {note && <>
        <NoteDetailHeader note={{ ...note, title, cardCount: cards.length, questionLocale: note.questionLocale || 'ko-KR', learningLocale: note.learningLocale || 'en-US' }} backLabel={v('backBooks')} onBack={() => navigate('/vocabulary')}
          cover={<img src={cover || defaultNoteCover} alt="" />}
          actions={<MapNoteActions onEdit={() => navigate(`/vocabulary/notes/${noteId}/edit`)} onCopy={copy} copySuccess={copySuccess}
            onToggleState={toggle} noteEnabled={note.isEnabled !== false} disabled={busy} stateSaving={busy} onDelete={() => setDeleteOpen(true)}
            renderMenuItems={close => <VocabularyDirectionMenuItems direction={note.vocabularyDirection || 'q_to_a'} questionLocale={note.questionLocale} learningLocale={note.learningLocale} disabled={busy} onChange={value => { close(); changeDirection(value); }} />}
            onPublish={original ? () => navigate(`/notes/${noteId}/publish`, { state: { returnTo: `/vocabulary/notes/${noteId}`, studyKind: 'vocabulary' } }) : undefined} />}
          primaryAction={<button type="button" className="note-detail-button note-detail-button-primary" disabled={busy || note.isEnabled === false || !cards.length} onClick={start}><Play size={18} aria-hidden="true" />{v('startLearning')}</button>} />
        <p className="vocabulary-note-direction">{v('direction')}: {vocabularyDirectionLabel(note.vocabularyDirection, note)}</p>
        {notice && <p className="vocabulary-notice" role="status">{notice}</p>}
        <VocabularyNoteStats cards={cards} title={title} />
        <section className="note-detail-cards vocabulary-note-cards" aria-label={v('totalCards', { count: cards.length })}>
          <header><h2>{v('totalCards', { count: cards.length })}</h2><div className="note-detail-card-actions">
            <CardFaceToggle showAnswers={showAnswersFirst} onToggle={() => setShowAnswersFirst(value => !value)} disabled={busy || !cards.length} />
            <button type="button" className="note-detail-button" disabled={busy} onClick={() => navigate(`/vocabulary/notes/${noteId}/edit`)}><Plus size={17} aria-hidden="true" />{t('notesUnified.addCard')}</button>
          </div></header>
          <label className="vocabulary-search"><Search size={18} aria-hidden="true" /><input type="search" aria-label={v('cardSearch')} placeholder={v('cardSearch')} value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} /></label>
          {!cards.length && <p className="vocabulary-notice">{v('cardEmpty')}</p>}
          {cards.length > 0 && !filtered.length && <p className="vocabulary-notice">{v('noResults')}</p>}
          <ol ref={listRef} className="flashcard-map-grid vocabulary-word-grid" start={visiblePage * PAGE_SIZE + 1}>{filtered.slice(visiblePage * PAGE_SIZE, (visiblePage + 1) * PAGE_SIZE).map(card => <li key={card.cardId || card.id || card._id}>
            <VocabularyWordCard card={card} direction={showAnswersFirst ? 'a_to_q' : 'q_to_a'} disabled={busy} onEdit={() => navigate(`/vocabulary/notes/${noteId}/edit`, { state: { editCardId: card.cardId || card.id || card._id } })} />
          </li>)}</ol>
          {totalPages > 1 && <nav className="vocabulary-card-pagination" aria-label={v('page')}><button type="button" disabled={visiblePage === 0} onClick={() => changePage(visiblePage - 1)} aria-label={v('previousPage')}><ChevronLeft size={20} /></button><span aria-live="polite">{visiblePage + 1} / {totalPages}</span><button type="button" disabled={visiblePage === totalPages - 1} onClick={() => changePage(visiblePage + 1)} aria-label={v('nextPage')}><ChevronRight size={20} /></button></nav>}
        </section>
      </>}
    </div>
    <ConfirmDialog isOpen={deleteOpen} title={v('deleteTitle')} message={v('deleteMessage', { title })} confirmLabel={v('delete')} tone="danger" onConfirm={remove} onCancel={() => setDeleteOpen(false)} />
  </main>;
}
